/**
 * RI EMS PCR Narrative Reservoir
 *
 * Compliance: Protocol 1.02 Documentation, RI Statewide EMS Protocols
 * and Standing Orders v2026.01 (effective 2026-04-01).
 *
 * Pre-audited sentence templates keyed by section + data point. Three
 * variants per slot prevent stylistic monotony across consecutive PCRs.
 * Templates are free of em dashes, en dashes, "WNL", and first-person
 * pronouns — Section 2 style rules. Slots are written in `[brackets]`
 * and filled by the assembler. Past tense throughout.
 *
 * Edit procedure (Section 11 ¶7): add or modify variants in this file,
 * run `pnpm verify:narrative`, commit with a CHANGELOG note referencing
 * the Protocol 1.02 paragraph the variant serves.
 */

export type Variants = readonly [string, string, string];

export const reservoir = {
  // ── Dispatch ────────────────────────────────────────────────
  dispatch: {
    arrival: [
      'Dispatched to [dispatch_location] for [dispatch_reason]. Crew arrived to find [arrival_finding].',
      'Crew was dispatched to [dispatch_location] for a [dispatch_reason] complaint and located [arrival_finding] on arrival.',
      'Response was initiated to [dispatch_location] for [dispatch_reason]. On arrival, the crew encountered [arrival_finding].',
    ] as Variants,
    arrival_minimal: [
      'Crew responded to [dispatch_reason]. The patient was located on arrival.',
      'EMS was dispatched for [dispatch_reason] and made patient contact on arrival.',
      'Response was completed to a call for [dispatch_reason]; patient contact was established at the scene.',
    ] as Variants,
  },

  // ── Chief complaint + MOI ───────────────────────────────────
  complaint: {
    medical: [
      'Chief complaint was [chief_complaint]. No traumatic mechanism.',
      'The patient presented with [chief_complaint] in the absence of trauma.',
      'Primary complaint at the time of contact was [chief_complaint].',
    ] as Variants,
    trauma: [
      'Chief complaint was [chief_complaint], secondary to [moi].',
      'The patient presented with [chief_complaint] following [moi].',
      'Mechanism of injury was [moi]; chief complaint was [chief_complaint].',
    ] as Variants,
  },

  // ── HPI / history ────────────────────────────────────────────
  hpi: [
    '[hpi]',
    'On further questioning, the patient reported that [hpi].',
    'History of present illness, as obtained from [history_source]: [hpi].',
  ] as Variants,
  history_source_other: [
    'History was obtained from [history_source] as the patient was unable to provide a reliable account.',
    'Information was gathered from [history_source] given the patient could not recount events.',
    'The history was provided by [history_source].',
  ] as Variants,
  pmh_present: [
    'Past medical history included [pmh].',
    'Reported past medical history was significant for [pmh].',
    'The patient had a documented history of [pmh].',
  ] as Variants,
  pmh_absent: [
    'The patient reported no significant past medical history.',
    'No past medical history was reported.',
    'Past medical history was unremarkable per the patient.',
  ] as Variants,
  meds_present: [
    'Home medications included [medications].',
    'The patient reported daily medications: [medications].',
    'Active prescriptions included [medications].',
  ] as Variants,
  meds_absent: [
    'The patient reported no daily medications.',
    'No prescription medications were reported.',
    'No home medications were on file at the time of contact.',
  ] as Variants,
  allergies_present: [
    'Allergies included [allergies].',
    'Documented allergies were [allergies].',
    'The patient reported the following allergies: [allergies].',
  ] as Variants,
  allergies_absent: [
    'The patient reported no known drug allergies.',
    'No known drug allergies were reported.',
    'NKDA was documented at the time of contact.',
  ] as Variants,
  family_social_relevant: [
    '[family_social_hx]',
    'Relevant social history: [family_social_hx].',
    'The following social history was relevant to the encounter: [family_social_hx].',
  ] as Variants,

  // ── Assessment / exam ────────────────────────────────────────
  exam: [
    '[exam_findings]',
    'Physical examination revealed [exam_findings].',
    'Focused examination demonstrated [exam_findings].',
  ] as Variants,
  vitals_first: [
    'Initial vital signs at [vitals_set_1.time]: BP [vitals_set_1.bp], pulse [vitals_set_1.pulse], respirations [vitals_set_1.rr], SpO2 [vitals_set_1.spo2] percent on [vitals_set_1.o2].',
    'First set of vitals at [vitals_set_1.time]: blood pressure [vitals_set_1.bp], pulse [vitals_set_1.pulse], respirations [vitals_set_1.rr], oxygen saturation [vitals_set_1.spo2] percent on [vitals_set_1.o2].',
    'At [vitals_set_1.time], BP was [vitals_set_1.bp], pulse [vitals_set_1.pulse], respirations [vitals_set_1.rr], and SpO2 [vitals_set_1.spo2] percent on [vitals_set_1.o2].',
  ] as Variants,
  vitals_repeat: [
    'Repeat at [vitals_set_2.time]: BP [vitals_set_2.bp], pulse [vitals_set_2.pulse], respirations [vitals_set_2.rr], SpO2 [vitals_set_2.spo2] percent on [vitals_set_2.o2].',
    'Reassessment at [vitals_set_2.time] showed BP [vitals_set_2.bp], pulse [vitals_set_2.pulse], respirations [vitals_set_2.rr], SpO2 [vitals_set_2.spo2] percent on [vitals_set_2.o2].',
    'A second set of vitals at [vitals_set_2.time]: BP [vitals_set_2.bp], pulse [vitals_set_2.pulse], respirations [vitals_set_2.rr], SpO2 [vitals_set_2.spo2] percent on [vitals_set_2.o2].',
  ] as Variants,
  ecg: [
    'A 12-lead ECG was acquired and interpreted as [ecg_interpretation]. Tracing was uploaded to the ePCR.',
    '12-lead ECG demonstrated [ecg_interpretation]; the tracing was uploaded to the ePCR.',
    'Twelve-lead acquisition revealed [ecg_interpretation]. Tracing was uploaded to the ePCR.',
  ] as Variants,
  bg: [
    'Blood glucose was [bg_value] mg/dL.',
    'Point-of-care glucose measured [bg_value] mg/dL.',
    'Capillary blood glucose returned [bg_value] mg/dL.',
  ] as Variants,
  field_dx: [
    'Field impression was [field_diagnosis].',
    'Presumptive field diagnosis was [field_diagnosis].',
    'Working diagnosis at the time of transfer was [field_diagnosis].',
  ] as Variants,
  protocols_invoked: [
    'Care was rendered under [protocols_invoked].',
    'Treatment was guided by [protocols_invoked].',
    'The encounter was managed in accordance with [protocols_invoked].',
  ] as Variants,

  // ── Interventions / Rx ───────────────────────────────────────
  // Medication administration. The bold wrapper is applied at
  // assembly time per Section 7 (restock workflow). [drug] / [dose]
  // / [route] / [time] / [performer] / [response] are filled.
  med_admin: [
    '[drug] [dose] [route] at [time] by the [performer]. [response]',
    'At [time], [drug] [dose] was administered [route] by the [performer]. [response]',
    'The [performer] administered [drug] [dose] [route] at [time]. [response]',
  ] as Variants,
  med_admin_assisted: [
    'The crew assisted the patient with the patient\'s own [drug] [dose] [route] at [time]. [response]',
    'At [time], the patient was assisted in self-administering the patient\'s own [drug] [dose] [route]. [response]',
    'The [performer] assisted the patient with self-administration of the patient\'s own [drug] [dose] [route] at [time]. [response]',
  ] as Variants,
  procedure: [
    '[procedure] was performed at [procedure_location] on the [procedure_attempt] attempt by the [procedure_performer] at [procedure_time] [procedure_outcome].',
    'At [procedure_time], the [procedure_performer] performed [procedure] at [procedure_location] on the [procedure_attempt] attempt [procedure_outcome].',
    'The [procedure_performer] completed [procedure] at [procedure_location] on the [procedure_attempt] attempt at [procedure_time] [procedure_outcome].',
  ] as Variants,
  oxygen: [
    'Supplemental oxygen was administered at [o2_rate] via [o2_route].',
    '[o2_rate] of supplemental oxygen was applied via [o2_route].',
    'Oxygen therapy at [o2_rate] was initiated via [o2_route].',
  ] as Variants,

  // ── Transport / hand-off ─────────────────────────────────────
  transport: [
    'Transported to [destination][alert_phrase].',
    'The patient was transported to [destination][alert_phrase] without incident.',
    'Transport was completed to [destination][alert_phrase].',
  ] as Variants,
  handoff: [
    'Care transferred to [receiving_provider_name], [receiving_provider_credential], at [handoff_time]. Verbal report included chief complaint, findings, interventions, and patient response. Receiving signature obtained.',
    'At [handoff_time], care was handed off to [receiving_provider_name], [receiving_provider_credential]. A verbal report covering chief complaint, findings, interventions, and patient response was provided, and receiving signature was obtained.',
    '[receiving_provider_name], [receiving_provider_credential], assumed care at [handoff_time]. Verbal report and receiving signature were completed.',
  ] as Variants,

  // ── Exception (DCHARTE E) ────────────────────────────────────
  exception_present: [
    '[exceptions]',
    'Notable circumstances during this encounter: [exceptions].',
    'The following deviations from routine were documented: [exceptions].',
  ] as Variants,
  exception_none: [
    'No exceptions or deviations from the standard of care were noted.',
    'The encounter proceeded without notable deviations from routine practice.',
    'No documentation exceptions were recorded for this call.',
  ] as Variants,

  // ── SBAR / SOAP / DISPATCH ad hoc lines ──────────────────────
  sbar_situation: [
    'The patient is a [patient_line] being transported for [reason_for_transport]. Current status is [current_status].',
    '[patient_line] is being moved to [destination] for [reason_for_transport]; status at hand-off is [current_status].',
    'A [patient_line] presented with [reason_for_transport]. Current status is [current_status].',
  ] as Variants,
  sbar_recommendation: [
    'Receiving provider was asked to [specific_ask].',
    'Hand-off included a request to [specific_ask].',
    'The clinical recommendation conveyed at hand-off was [specific_ask].',
  ] as Variants,
};

/**
 * Variant selector. Round-robin against a session-stable index so
 * consecutive sentences within the same draft don't all pick variant 0,
 * but two consecutive PCRs aren't byte-identical either.
 */
export function pick<T>(variants: readonly T[], seed: number): T {
  if (variants.length === 0) throw new Error('reservoir: empty variant list');
  return variants[Math.abs(seed) % variants.length];
}
